'use client'

// React Imports
import { useEffect, useRef, useState, useTransition } from 'react'

// Next Imports
import { usePathname, useRouter } from 'next/navigation'

// MUI Imports
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import type { Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

// Third-party Imports
import classnames from 'classnames'

// Component Imports
import ProgressBar from '@/components/ProgressBar'
import DropdownMenu from './DropdownMenu'

type Props = {
  isDrawerOpen: boolean
  setIsDrawerOpen: (open: boolean) => void
}

type WrapperProps = {
  children: React.ReactNode
  isBelowLgScreen: boolean
  className?: string
  isDrawerOpen: boolean
  setIsDrawerOpen: (open: boolean) => void
}

const Wrapper = (props: WrapperProps) => {
  // Props
  const { children, isBelowLgScreen, className, isDrawerOpen, setIsDrawerOpen } = props
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  if (isBelowLgScreen) {
    return (
      <Drawer
        variant='temporary'
        anchor='right'
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        ModalProps={{
          keepMounted: true
        }}
        sx={{ '& .MuiDrawer-paper': { width: ['100%', 300] } }}
        className={classnames('p-5', className)}
      >
        <div className='p-4 flex flex-col gap-x-3'>
          <IconButton onClick={() => setIsDrawerOpen(false)} className='absolute inline-end-4 block-start-2'>
            <i className={`ri-close-line ${isMobile ? 'text-primary' : 'text-primary'}`} />
          </IconButton>
          {children}
        </div>
      </Drawer>
    )
  }

  return <div className={classnames('flex items-center flex-wrap gap-x-4 gap-y-3', className)}>{children}</div>
}

const FrontMenu = (props: Props) => {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  // Props
  const { isDrawerOpen, setIsDrawerOpen } = props

  // Hooks
  const pathname = usePathname()
  const isBelowLgScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const [activeSection, setActiveSection] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['image-slider', 'obituary', 'about', 'services', 'facilities', 'free-program'] // Add all your section IDs

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId)

        if (section) {
          const rect = section.getBoundingClientRect()

          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            setActiveSection(sectionId)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    
return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!isBelowLgScreen && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBelowLgScreen])

  return (
    <>
      {isPending && <ProgressBar />}
      <Wrapper isBelowLgScreen={isBelowLgScreen} isDrawerOpen={isDrawerOpen} setIsDrawerOpen={setIsDrawerOpen}>
        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={() => {
            setIsDrawerOpen(false)
            startTransition(() => {
              if (pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              } else {
                router.push('/')
              }
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Home
          <span
            className={classnames(
              'block scale-x-0 h-[2px] transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': activeSection === 'image-slider',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>

        {/* <DropdownMenu
          isBelowLgScreen={isBelowLgScreen}
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        /> */}

        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={async () => {
            setIsDrawerOpen(false)
            startTransition(async () => {
              if (pathname === '/') {
                const element = document.getElementById('about')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }

                return
              }

              router.push('/tentang-kami')
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Tentang Kami
          <span
            className={classnames(
              'block scale-x-0 h-[2px]  transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/tentang-kami' || (pathname === '/' && activeSection === 'about'),
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>

        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={async () => {
            setIsDrawerOpen(false)
            startTransition(async () => {
              // Mark this callback function as async
              if (pathname === '/') {
                const element = document.getElementById('services')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              } else {
                await router.push('/')
                const element = document.getElementById('services')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              }
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Layanan
          <span
            className={classnames(
              'block scale-x-0 h-[2px]  transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/services' || activeSection === 'services',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>
        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={async () => {
            setIsDrawerOpen(false)
            startTransition(async () => {
              // Mark this callback function as async
              if (pathname === '/') {
                const element = document.getElementById('facilities')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              } else {
                await router.push('/')
                const element = document.getElementById('facilities')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              }
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Fasilitas
          <span
            className={classnames(
              'block scale-x-0 h-[2px]  transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/facilities' || activeSection === 'facilities',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>
        {/* <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={async () => {
            setIsDrawerOpen(false)
            startTransition(async () => {
              // Mark this callback function as async
              if (pathname === '/') {
                const element = document.getElementById('free-program')
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              } else {
                await router.push('/')
                const element = document.getElementById('free-program')
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              }
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Program Gratis
          <span
            className={classnames(
              'block scale-x-0 h-[2px]  transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/free-program' || activeSection === 'free-program',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography> */}
        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={async () => {
            setIsDrawerOpen(false)
            startTransition(async () => {
              if (pathname === '/') {
                const element = document.getElementById('obituary')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              } else {
                await router.push('/')
                const element = document.getElementById('obituary')

                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' })
                }
              }
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Informasi Kedukaan
          <span
            className={classnames(
              'block scale-x-0 h-[2px]  transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/obituary' || activeSection === 'obituary',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>
        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={() => {
            setIsDrawerOpen(false)
            startTransition(() => {
              router.push('/artikel')
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Artikel
          <span
            className={classnames(
              'block scale-x-0 h-[2px] transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/artikel' || pathname.startsWith('/artikel') || (pathname !== '/' && pathname !== '/tentang-kami' && pathname !== '/kontak' && !pathname.startsWith('/order')),
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>
        <Typography
          color='text-primary'
          sx={{
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'color 0.3s ease',
            '&:hover': {
              color: 'primary.main'
            }
          }}
          onClick={() => {
            setIsDrawerOpen(false)
            startTransition(() => {
              router.push('/kontak')
            })
          }}
          className={classnames('font-medium plb-3 pli-1.5 group', isMobile ? 'text-primary' : 'text-primary')}
        >
          Kontak
          <span
            className={classnames(
              'block scale-x-0 h-[2px] transition-all duration-300 mt-1 origin-left',
              {
                'scale-x-100': pathname === '/kontak',
                'group-hover:scale-x-100': true
              },
              isMobile ? 'bg-primary' : 'bg-primary'
            )}
          />
        </Typography>
      </Wrapper>
    </>
  )
}

export default FrontMenu
